import { StorefrontAccess } from "@insite/client-framework/Services/SettingsService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ApplicationMemberAppConfigState } from "@thkStore/Store/Components/MemberAppConfig/MemberAppConfigReducer";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import getColor from "@insite/mobius/utilities/getColor";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import { AUTH_SSO_SIGN_IN } from "@thkStore/Common/Constants/AuthConstants";

interface OwnProps extends WidgetProps {
    fields: {
        buttonText: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const memberAppConfig = (state as ApplicationMemberAppConfigState).memberAppConfig;
    return {
        session: state.context.session,
        settingsCollection: getSettingsCollection(state),
        signInPageLink: getPageLinkByPageType(state, "SignInPage"),
        thkMemberAppUrl: memberAppConfig?.thkMemberAppUrl || "",
        enableThkMemberAppRedirection: memberAppConfig?.enableThkMemberAppRedirection || false,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductDetailsLoginButtonStyles {
    loginButton?: ButtonPresentationProps;
}

export const loginButtonStyles: ProductDetailsLoginButtonStyles = {
    loginButton: {
        css: css`
            color: ${getColor("primary.contrast")};
            background-color: ${getColor("primary.main")};
            width: 100%;
            padding: 18px;
            margin: 25px 0 40px;
            border-radius: 0;

            &:hover {
                color: ${getColor("primary.main")} !important;
                background-color: ${getColor("primary.contrast")} !important;
            }

            span {
                font-size: 24px;
            }
        `,
    },
};

const styles = loginButtonStyles;

const ProductDetailsLoginButton: React.FC<Props> = ({
    session,
    settingsCollection,
    fields,
    signInPageLink,
    thkMemberAppUrl,
    enableThkMemberAppRedirection,
}) => {
    if (session.isAuthenticated && !session.isGuest) {
        return null;
    }
    const productSettings = settingsCollection.productSettings;

    if (
        productSettings.storefrontAccess !== StorefrontAccess.SignInRequiredToAddToCart &&
        productSettings.storefrontAccess !== StorefrontAccess.SignInRequiredToAddToCartOrSeePrices
    ) {
        return null;
    }

    const handleClick = () => {
        const currentPageUrl = window.location.pathname + window.location.search;
        let redirectUrl: string;        
        if (enableThkMemberAppRedirection && thkMemberAppUrl) {
            // Member App redirection (reads config from Redux store, set by HeaderSignIn widget)
            const autoSsoUrl = `${window.location.origin}${AUTH_SSO_SIGN_IN}`;
            redirectUrl = `${thkMemberAppUrl}?red=${autoSsoUrl}?returnUrl=${currentPageUrl}`;
        } else {
            // Default fallback logic
            const loginSettingSettings = (settingsCollection as any)["loginSettingSettings"] || "";
            const loginRedirectUrl = loginSettingSettings?.loginRedirectUrl || "";
            const baseUrl = loginRedirectUrl || signInPageLink?.url || AUTH_SSO_SIGN_IN;
            redirectUrl = `${baseUrl}?returnUrl=${encodeURIComponent(currentPageUrl)}`;
        }

        window.location.href = redirectUrl;
    };

    return (
        <Button {...styles.loginButton} onClick={handleClick} data-test-selector="productDetailsLoginButton">
            {fields.buttonText || translate("Log in to add products to the cart")}
        </Button>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductDetailsLoginButton),
    definition: {
        displayName: "Login Button (Product Details)",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: "buttonText",
                displayName: "Button Text",
                editorTemplate: "TextField",
                defaultValue: "Log in to add products to the cart.",
                fieldType: "Translatable",
                isRequired: false,
                tooltip: "Text displayed on the login button. If empty, the default value is used.",
            },
        ],
    },
};

export default widgetModule;
